import React from 'react';
import { Link as RRLink } from 'react-router-dom';
import { AbstractButton, Badge, Box, Flex, Icon, Link, Text } from 'pounceV2';
import urls from 'Source/urls';
import { AISummarizeKind } from 'Generated/schema';
import GenericCodeBlockV3 from 'Components/CodeBlock/GenericCodeBlockV3';
import ToolInvocationV3 from 'Components/AIExplainLogEvents/ToolInvocationV3';
import { useTheme } from '@mui/material';
import {
  useGenericAISummaryContextV3,
  getAiOutputLengthSetting,
} from '../AIExplainLogEvents/GenericAISummaryContextV3';

type PantherCodeBlockProps = {
  children: React.ReactNode;
  onPromptClick?: (prompt: string) => void;
  suppress?: boolean;
};

const PantherCodeBlockV3 = ({ children, onPromptClick, suppress }: PantherCodeBlockProps) => {
  const {
    palette: { tokens, componentTokens },
  } = useTheme();
  const content = React.Children.toArray(children).join('');
  const context = useGenericAISummaryContextV3();
  const kind = context?.kind;
  const outputLength = getAiOutputLengthSetting(kind);
  const rerun = context?.rerun;
  const pantherContent = content.replace(/^panther\s*/i, '').trim();
  // Legacy: older PantherFlow tool responses emitted the validated query as a
  // `pfQuery=<query>` citation. The current tool emits a normal markdown
  // `[Run in Log Search](...)` link instead, but historical AI responses /
  // saved Kendra entries may still contain the old citation syntax — keep the
  // pfQuery match so they continue to render as a "Try in Search" link.
  // pfQuery needs [\s\S]+ to capture multiline PantherFlow queries; other types use [^\s]+.
  const pfQueryMatch = pantherContent.match(/^(pfQuery)=([\s\S]+)/);
  const referenceMatch =
    pfQueryMatch ||
    pantherContent.match(
      /^(alertId|queryId|savedQueryId|ruleId|policyId|scheduledRuleId|correlationRuleId|scheduledQueryId|toolUseId|prUrl)=([^\s]+)/,
    );
  const promptMatch = pantherContent.match(/prompt="([^"]+)"/);
  if (!referenceMatch && !promptMatch) {
    return <GenericCodeBlockV3 language="text">{content}</GenericCodeBlockV3>;
  }

  if (referenceMatch && referenceMatch[1] === 'toolUseId') {
    const toolNameMatch = pantherContent.match(/toolName=([^ ]+)/);
    const toolName = toolNameMatch ? toolNameMatch[1] : undefined;
    return <ToolInvocationV3 toolInvocationId={referenceMatch[2]} toolName={toolName} />;
  }

  if (referenceMatch && referenceMatch[1] === 'prUrl') {
    const url = referenceMatch[2];
    return (
      <Flex my={3}>
        <Link href={url} external textDecoration="none">
          <Badge
            borderColor={componentTokens.badges.colors.outline.brand.stroke.default}
            backgroundColor={componentTokens.badges.colors.outline.brand.background.standard}
            display="block"
            color={componentTokens.badges.colors.outline.brand.text.default}
            _hover={{
              backgroundColor: componentTokens.badges.colors.solid.info.background.standard,
              borderColor: componentTokens.badges.colors.solid.info.stroke.default,
            }}
          >
            <Flex alignItems="center" justifyContent="center">
              <Box as="span" mx={1}>
                View PR
              </Box>
              <Icon mx={1} type="external-link" size="x-small" />
            </Flex>
          </Badge>
        </Link>
      </Flex>
    );
  }

  if (promptMatch && !referenceMatch) {
    const [, prompt] = promptMatch;
    // Use line-boundary guard to prevent false matches inside prompt content.
    const titleMatch = pantherContent.match(/(?:^|\r?\n)\s*title="([^"]+)"/);
    const displayText = titleMatch ? titleMatch[1] : prompt;
    // Safety net: when the parent renders a rich RecommendedActionsV3 card for this message,
    // suppress inline prompt links to prevent double-rendering.
    if (suppress) {
      return null;
    }
    // If neither rerun (non-conversational) nor onPromptClick (conversational) is available,
    // just display the prompt as text
    if (!rerun && !onPromptClick) {
      return (
        <Text color={tokens.content.text.accent[14].base} py={1} pl={2}>
          {displayText}
        </Text>
      );
    }
    return (
      <AbstractButton
        fontWeight="medium"
        display="flex"
        alignItems="center"
        textAlign="left"
        color={tokens.content.text.accent[14].base}
        py={1}
        pl={2}
        _hover={{ textDecoration: 'underline' }}
        onClick={() => {
          if (onPromptClick) {
            onPromptClick(prompt);
          } else {
            rerun({
              additionalPrompt: prompt,
              kind: AISummarizeKind.DrillDown,
              outputLength,
            });
          }
        }}
      >
        {displayText}
      </AbstractButton>
    );
  }

  const [, referenceType, id] = referenceMatch;

  let linkType = '';
  let path = '';

  if (referenceType === 'alertId') {
    linkType = 'Alert';
    path = urls.alerts.details(id);
  } else if (referenceType === 'queryId') {
    linkType = 'Query';
    if (pantherContent.includes('data-explorer')) {
      path = `${urls.investigate.dataExplorer()}?queryId=${id}`;
    } else {
      path = urls.investigate.search({ queryId: id });
    }
  } else if (referenceType === 'savedQueryId') {
    linkType = 'Saved Query';
    path = `${urls.investigate.dataExplorer()}?savedQueryId=${id}`;
  } else if (referenceType === 'ruleId') {
    linkType = 'Rule';
    path = urls.build.detections.rules.details(id);
  } else if (referenceType === 'policyId') {
    linkType = 'Policy';
    path = urls.build.detections.policies.details(id);
  } else if (referenceType === 'scheduledRuleId') {
    linkType = 'Scheduled Rule';
    path = urls.build.detections.rules.details(id);
  } else if (referenceType === 'correlationRuleId') {
    linkType = 'Correlation Rule';
    path = urls.build.detections.correlationRules.details(id);
  } else if (referenceType === 'scheduledQueryId') {
    linkType = 'Scheduled Query';
    path = `${urls.investigate.dataExplorer()}?savedQueryId=${id}`;
  } else if (referenceType === 'pfQuery') {
    // Legacy citation path — see the comment on `pfQueryMatch` above.
    linkType = 'Try in Search';
    path = urls.investigate.search({ usePantherFlow: true, pfQuery: id.trim() });
  }

  const citationText = referenceType === 'pfQuery' ? linkType : `${linkType} ${id}`;

  return (
    <Flex my={3}>
      <Link as={RRLink} to={path} target="_blank" textDecoration="none">
        <Badge
          borderColor={componentTokens.badges.colors.outline.brand.stroke.default}
          backgroundColor={componentTokens.badges.colors.outline.brand.background.standard}
          display="block"
          color={componentTokens.badges.colors.outline.brand.text.default}
          _hover={{
            backgroundColor: componentTokens.badges.colors.solid.info.background.standard,
            borderColor: componentTokens.badges.colors.solid.info.stroke.default,
          }}
        >
          <Flex alignItems="center" justifyContent="center" gap={2}>
            <Text>{citationText}</Text>
            <Icon type="external-link" size="x-small" />
          </Flex>
        </Badge>
      </Link>
    </Flex>
  );
};

export default React.memo(PantherCodeBlockV3);
